import React, { useEffect } from 'react';
import { TextInput, FormControl, Button, Stack } from '@primer/react';
import { ClockIcon } from '@primer/octicons-react';

interface TimeInputProps {
    value: string;
    onChange: (value: string) => void;
    type?: 'from' | 'to';
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    date?: string;
}

const TimeInput: React.FC<TimeInputProps> = ({
    value,
    onChange,
    className = '',
    disabled = false,
    date
}) => {
    const today = new Date().toISOString().split('T')[0];
    const isToday = date && date === today;
    const minTime = isToday ? new Date().toTimeString().slice(0, 5) : undefined;

    useEffect(() => {
        if (minTime && value && value < minTime) {
            onChange(minTime);
        }
    }, [minTime, value, onChange]);

    const handleTimeChange = (newValue: string) => {
        if (!newValue) {
            onChange(newValue);
            return;
        }

        if (minTime && newValue < minTime) {
            return;
        }
        onChange(newValue);
    };


    return (
        <div className={`relative ${className}`}>
            <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={16} />
            <input
                type="time"
                value={value}
                onChange={(e) => handleTimeChange(e.target.value)}
                min={minTime}
                disabled={disabled}
                style={{ height: '40px' }}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
        </div>
    );
};

interface TimeRangeInputProps {
    fromTime: string;
    toTime: string;
    onFromTimeChange: (time: string) => void;
    onToTimeChange: (time: string) => void;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    date?: string;
}

const TimeRangeInput: React.FC<TimeRangeInputProps> = ({
    fromTime,
    toTime,
    onFromTimeChange,
    onToTimeChange,
    className = '',
    disabled = false,
    error,
    date
}) => {
    return (
        <Stack direction="horizontal">
            <FormControl required key='from'>
                <FormControl.Label className='invisible h-0'></FormControl.Label>
                <TimeInput
                    value={fromTime}
                    onChange={onFromTimeChange}
                    type="from"
                    disabled={disabled}
                    date={date}
                    className='!w-[140px]'
                />
                {error && (
                    <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
                )}
            </FormControl>
            <FormControl required key='to'>
                <FormControl.Label className='invisible h-0'></FormControl.Label>
                <TimeInput
                    value={toTime}
                    onChange={onToTimeChange}
                    type="to"
                    disabled={disabled}
                    date={date}
                    className='!w-[140px]'
                />
                {error && (
                    <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
                )}
            </FormControl>
        </Stack>
    );
};

export { TimeInput, TimeRangeInput }